// Post-auth 18+ self-attestation gate. Rendered by ProtectedRoute IN PLACE (the URL
// never changes) when an authenticated user has not yet attested, so once they
// confirm, the underlying route — landing chain, returnTo/invite join, KYC return —
// resolves exactly as it would have. There is no way past it but to confirm or sign
// out; declining signs the user out rather than letting them into the app unattested.
//
// This is a SELF-DECLARATION with NO enforcement weight. The real eligibility gate is
// identity verification (Persona/KYC) at the money moment.
import { useState } from 'react';
import { ShieldCheck, Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useRecord18PlusAttestation } from '@/hooks/useAttestation';
import sabotagelogo from '@/assets/sabotage-logo.png';

const ATTESTATION_COPY =
  'I confirm I am 18 or older (19 in Nebraska) and legally eligible to play in my state.';

export function AttestationGate() {
  const { user, signOut } = useAuth();
  const record = useRecord18PlusAttestation(user?.id);
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onContinue = async () => {
    if (!checked || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      // On success the cached status flips to true → ProtectedRoute re-renders the
      // real route in place. This component simply unmounts; no navigation.
      await record();
    } catch {
      setError('We could not save your confirmation. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center px-5">
      <div className="w-full max-w-[420px]">
        <img src={sabotagelogo} alt="Sabotage Fantasy" className="h-11 w-auto mx-auto mb-8" />

        <h1 className="text-center font-display font-bold text-[22px] text-foreground">
          One quick confirmation
        </h1>
        <p className="mt-2 text-center text-[13.5px] text-muted-foreground leading-[1.5]">
          Before you continue, please confirm you're eligible to play.
        </p>

        <label
          className="mt-7 flex items-start gap-3 rounded-xl p-4 cursor-pointer select-none"
          style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.14)' }}
        >
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
            className="mt-0.5 h-5 w-5 shrink-0 accent-[hsl(var(--primary))]"
            aria-describedby="attest-note"
          />
          <span className="text-[14px] text-foreground leading-[1.5]">{ATTESTATION_COPY}</span>
        </label>

        <p id="attest-note" className="mt-2.5 flex items-start gap-2 text-[11.5px] text-muted-foreground/80 leading-[1.5]">
          <ShieldCheck className="h-3.5 w-3.5 text-[hsl(var(--primary))] shrink-0 mt-0.5" />
          This is a self-declaration. We verify identity separately before any deposit or withdrawal.
        </p>

        {error && <p className="mt-3 text-[13px] text-red-400">{error}</p>}

        <button
          type="button"
          onClick={onContinue}
          disabled={!checked || submitting}
          className="mt-6 h-[54px] w-full rounded-xl font-display font-bold text-[17px] text-primary-foreground bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary)/0.9)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {submitting ? <><Loader2 className="h-5 w-5 animate-spin" />Saving…</> : 'Continue'}
        </button>

        <button
          type="button"
          onClick={() => void signOut()}
          disabled={submitting}
          className="mt-4 w-full text-center text-[13px] text-muted-foreground hover:text-foreground disabled:opacity-50"
        >
          I don't confirm — sign out
        </button>
      </div>
    </div>
  );
}
